import { useState, useEffect, useRef, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  generateFromKnowledge,
  saveQuestions,
  updateQuestions,
  getLessonName,
  fetchExamBank,
  uploadKnowledgeText,
  deleteQuestions
} from '@/api/kuizy'
import KnowledgeUploader from '@/widgets/KnowledgeUploader'
import CommonSettings from '@/widgets/CommonSettings'
import ManualQuestionForm from '@/widgets/ManualQuestionForm'
import QuestionsCard from '@/widgets/QuestionsCard'
import ConfirmModal from '@/widgets/ConfirmModal'
import {
  levelMapping,
  examTypeMapping,
  typeSummaryMapping,
  LANGUAGES_AI,
  localeToLabel,
  browserLocale
} from '@/settings/master'
import {
  Trash2,
  ArrowLeft,
  BookOpen,
  Wand2,
  ListChecks,
  Redo2,
  Plus,
  Loader2,
  Mountain,
  MessageCircleMore,
  MessageCircleQuestion,
  Layers3,
  ChevronRight,
  List as ListIcon,
  AlertTriangle,
  Brain,
} from 'lucide-react'
import { LayoutContext } from "../layout/KuizyLayout";
import ImportExamModal from '../widgets/ImportExamModal'

function Stepper({ current, onChange }) {
  const items = [
    { icon: BookOpen, text: 'คลังความรู้' },
    { icon: Wand2, text: 'ตั้งค่าและสร้างข้อสอบ' },
    { icon: ListChecks, text: 'จัดการข้อสอบ' }
  ]
  return (
    <div className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-28 my-6">
      {items.map((it, i) => {
        const idx = i + 1
        const state = idx < current ? 'done' : idx === current ? 'active' : 'upcoming'
        const Icon = it.icon
        return (
          <div
            key={idx}
            className="relative flex flex-col items-center cursor-pointer select-none"
            onClick={() => (state !== 'upcoming' ? onChange(idx) : null)}
          >
            <div
              className={[
                'w-12 h-12 rounded-full flex items-center justify-center text-white text-xl transition-colors z-50',
                state === 'done' && 'bg-secondary',
                state === 'active' && 'bg-primary',
                state === 'upcoming' && 'bg-gray-300'
              ].join(' ')}
            >
              <Icon size={22} />
            </div>
            {idx < items.length && (
              <div className="hidden md:block absolute top-1/3 left-full w-20 h-1 bg-gray-200 translate-x-4" />
            )}
            <span
              className={[
                'mt-2 text-sm font-semibold',
                state === 'done' && 'text-secondary',
                state === 'active' && 'text-primary',
                state === 'upcoming' && 'text-gray-400'
              ].join(' ')}
            >
              {it.text}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export default function CreateExamPage() {
  const { cid: cidRaw, lid: lidRaw } = useParams()
  const navigate = useNavigate()
  const cid = decodeURIComponent(cidRaw || '')
  const lid = lidRaw ? decodeURIComponent(lidRaw) : ''
  const uid = localStorage.getItem('userId') || '1'

  const initLang = () => {
    const label = localeToLabel(browserLocale || 'th')
    const e = LANGUAGES_AI.find(l => l.label === label)
    return e ? e.name : 'Thai'
  }

  const [lessonName, setLessonName] = useState('')
  const [level, setLevel] = useState('2')
  const [examType, setExamType] = useState('multipleChoice')
  const [optionStyle, setOptionStyle] = useState('thai')
  const [typeSummary, setTypeSummary] = useState('TF')
  const [typeSummaryMin, setTypeSummaryMin] = useState(1)
  const [typeSummaryMax, setTypeSummaryMax] = useState(4)
  const [questionCount, setQuestionCount] = useState(10)
  const [optionCount, setOptionCount] = useState(4)
  const [examLanguage, setExamLanguage] = useState(initLang)
  const [extraPrompt, setExtraPrompt] = useState('')
  const [questions, setQuestions] = useState([])

  const [sourceMode, setSourceMode] = useState('file')
  const [uploadBusy, setUploadBusy] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [step, setStep] = useState(1)

  const [showManualPopup, setShowManualPopup] = useState(false)
  const [manualOptions, setManualOptions] = useState(Array(optionCount).fill(0).map(() => ({ desc: '', points: 1 })))
  const [manualCorrect, setManualCorrect] = useState([])
  const [newQuestionText, setNewQuestionText] = useState('')

  const [editingIndex, setEditingIndex] = useState(null)
  const [editedQuestion, setEditedQuestion] = useState(null)

  const [alertMsg, setAlertMsg] = useState('')
  const [showAlert, setShowAlert] = useState(false)
  const [confirmRe, setConfirmRe] = useState(false)
  const [confirmGen, setConfirmGen] = useState(false)

  // ใหม่: เก็บสถิติสรุปคลังความรู้ + modal gate
  const uploaderRef = useRef(null)
  const [customText, setCustomText] = useState('')
  const [customId, setCustomId] = useState(null)
  const [customMode, setCustomMode] = useState('create') // create | edit
  const [shuffleOptions, setShuffleOptions] = useState(false)

  const [unsumCount, setUnsumCount] = useState(0)
  const [sumStats, setSumStats] = useState({ total: 0, summarized: 0, unsummarized: 0, tokens: 0 })
  const [showSummaryGate, setShowSummaryGate] = useState(false)

  const [selectedNew, setSelectedNew] = useState(new Set())
  const [selectedOld, setSelectedOld] = useState(new Set())
  const [confirmState, setConfirmState] = useState(null)

  const closeConfirm = () => setConfirmState(null)

  const { showLoading, hideLoading } = useContext(LayoutContext);

  const alert = m => { setAlertMsg(m); setShowAlert(true) }

  useEffect(() => {
    setManualOptions(Array(optionCount).fill(0).map(() => ({ desc: '', points: 1 })))
    setManualCorrect([])
  }, [optionCount])

  useEffect(() => {
    if (!lid) {
      alert('ไม่พบรหัสบทเรียน (lesson_id)')
      return
    }
    getLessonName(lid)
      .then(({ data }) => setLessonName(data.lesson_name || data.lesson || ''))
      .catch(() => { })

    fetchExamBank({
      user_id: uid,
      content_id: cid,
      lesson_id: lid, page: 1, pageSize: 'all'
    })
      .then(({ data }) => {
        const list = (data?.data || []).map(q => ({ ...q, _isNew: false }))
        setQuestions(list)
        if (list.length) {
          const optLen = Array.isArray(list[0].answer)
            ? list[0].answer.length
            : (typeof list[0].answer === 'object' ? Object.keys(list[0].answer || {}).length : 0)
          setOptionCount(optLen || 4)
          setLevel(String(list[0].difficult_level || level))
          setTypeSummary(list[0].type_summary || typeSummary)
          setExamType(list[0].exam_type || examType)
          setOptionStyle(list[0].option_style || optionStyle)
        }
      })
      .catch(() => alert('โหลดข้อสอบไม่สำเร็จ'))
  }, [lid])

  const doGenerate = async () => {
    setConfirmRe(false)
    if (!cid || !lid || !lessonName.trim()) {
      alert('กรอกข้อมูลให้ครบ')
      return
    }
    setGenerating(true)
    showLoading({
      variant: "imageSplit",
      text: "กำลังสร้างข้อสอบ...",
      subtext: "กรุณารอสักครู่",
      imageUrl: "/images/write-exam-2.gif",
    });
    try {
      const avoidQuestions = oldQs.map(q => q.question).filter(Boolean)
      const payload = {
        exam_id: lid,
        question: questionCount,
        option: optionCount,
        level,
        examType,
        typeSummary,
        typeSummaryMin,
        typeSummaryMax,
        language: examLanguage,
        prompt: extraPrompt,
        contentCode: cid,
        examLesson: lessonName,
        batchSize: 50,
        avoidQuestions,
        shuffle_options: shuffleOptions,
      }
      const { data } = await generateFromKnowledge(payload)
      const ready = (Array.isArray(data) ? data : []).map(q => ({
        id: q.id,
        lesson_id: lid,
        lesson_name: lessonName,
        content_id: cid,
        difficult_level: q.difficult_level || level,
        question: q.question,
        answer: q.answer,
        correct: q.correct,
        created_by: uid,
        createdAt: new Date().toISOString(),
        _isNew: true
      }))
      setQuestions(prev => [...ready, ...prev])
      alert(`สร้างสำเร็จ ${ready.length} ข้อ`)
      setStep(3)
    } catch (e) {
      alert(e.message || 'สร้างข้อสอบไม่สำเร็จ')
    } finally {
      setGenerating(false)
      hideLoading();
    }
  }


  const genSummary = () => {
    const langLabel = LANGUAGES_AI.find(l => l.name === examLanguage)?.label || examLanguage
    return (
      <ul className="list-none p-0 m-0 text-sm space-y-2">
        <li><b>ภาษา:</b> <span className="font-bold text-red-500">{langLabel}</span></li>
        <li><b>ระดับความยาก:</b> {levelMapping[level]}</li>
        <li><b>ประเภทข้อสอบ:</b> {examTypeMapping[examType]}</li>
        <li><b>จำนวนข้อสอบ:</b> {questionCount} ข้อ</li>
        <li><b>จำนวนคำตอบ:</b> {optionCount} ตัวเลือก</li>
        <li><b>ประเภทคำตอบ:</b> {typeSummaryMapping[typeSummary]}</li>
      </ul>
    )
  }

  const handleGenerateClick = () => setConfirmGen(true)
  const handleRegenerate = () => setConfirmRe(true)

  const addManualQuestion = () => {
    if (!newQuestionText.trim()) { alert('กรุณาป้อนคำถาม'); return }
    if (manualOptions.some(o => !o.desc.trim())) { alert('กรุณาป้อนตัวเลือกให้ครบ'); return }
    if (manualCorrect.length === 0 && typeSummary !== 'PO') { alert('เลือกคำตอบถูกต้องอย่างน้อย 1 ค่า'); return }
    const answerObjArr = manualOptions.map((o, idx) => ({
      [`option${idx + 1}`]: typeSummary === 'PO'
        ? { description: o.desc, points: o.points }
        : o.desc
    }))
    const newQ = {
      lesson_id: lid,
      lesson_name: lessonName,
      content_id: cid,
      question: newQuestionText,
      answer: answerObjArr,
      correct: [...manualCorrect],
      difficult_level: level,
      created_by: uid,
      createdAt: new Date().toISOString(),
      _isNew: true
    }
    setQuestions(p => [newQ, ...p])
    setShowManualPopup(false)
    setNewQuestionText('')
    setManualOptions(Array(optionCount).fill(0).map(() => ({ desc: '', points: 1 })))
    setManualCorrect([])
  }

  const openEditGlobal = gIdx => {
    setEditingIndex(gIdx)
    setEditedQuestion({ ...questions[gIdx] })
  }
  const handleSaveEdit = () => {
    if (!editedQuestion) return
    const cp = [...questions]
    cp[editingIndex] = { ...editedQuestion, updatedAt: new Date().toISOString() }
    setQuestions(cp)
    setEditingIndex(null)
    setEditedQuestion(null)
  }
  const handleDeleteGlobal = gIdx => {
    if (questions.length === 1) { alert('ต้องมีข้อสอบอย่างน้อย 1 ข้อ'); return }
    setQuestions(p => p.filter((_, i) => i !== gIdx))
  }

  const makeCardProps = idxMap => ({
    levelMapping,
    optionStyle,
    typeSummary,
    editingIndex: idxMap.indexOf(editingIndex),
    setEditingIndex: localIdx => {
      if (localIdx === null || localIdx === -1) { setEditingIndex(null); setEditedQuestion(null); return }
      const gIdx = idxMap[localIdx]
      if (gIdx !== undefined) openEditGlobal(gIdx)
    },
    editedQuestion,
    setEditedQuestion,
    startEditQuestion: localIdx => openEditGlobal(idxMap[localIdx]),
    handleSaveEdit,
    handleDeleteQuestion: localIdx => handleDeleteGlobal(idxMap[localIdx]),
    openAlert: alert
  })

  const saveToDB = async () => {
    if (saving) return
    if (!lid) { alert('ไม่พบรหัสบทเรียน (lesson_id)'); return }
    setSaving(true)
    const qPayload = questions.map(q => ({
      id: q.id,
      lesson_id: lid,
      lesson_name: lessonName,
      content_id: cid,
      question: q.question,
      answer: q.answer,
      correct: q.correct,
      difficult_level: q.difficult_level || level,
      created_by: q.created_by || uid
    }))

    try {
      if (questions.some(q => !q._isNew)) {
        await updateQuestions({}, qPayload)
      } else {
        await saveQuestions({ questions: qPayload })
      }
      navigate(`/content/${cidRaw}/lessons/${lid}/bank`, { replace: true })
    } catch (e) {
      alert(e.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const { newQs, oldQs, newMap, oldMap } = questions.reduce(
    (acc, q, idx) => {
      if (q._isNew) {
        acc.newQs.push(q)
        acc.newMap.push(idx)
      } else {
        acc.oldQs.push(q)
        acc.oldMap.push(idx)
      }
      return acc
    },
    { newQs: [], oldQs: [], newMap: [], oldMap: [] }
  )

  const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.35 } }

  const handleNextFromStep1 = async () => {
    if (unsumCount > 0) {
      setShowSummaryGate(true)
      return
    }

    setStep(2)
  }

  const confirmDeleteSelectedNew = () => {
    setConfirmState({
      title: 'ยืนยันการลบ',
      message: `ต้องการลบข้อสอบใหม่ที่เลือก ${selectedNew.size} ข้อ ใช่หรือไม่ ?`,
      onConfirm: async () => {
        setQuestions(prev => {
          const toDelete = new Set([...selectedNew].map(i => newMap[i]))
          return prev.filter((_, idx) => !toDelete.has(idx))
        })

        setSelectedNew(new Set())
        setEditingIndex(null)
        setEditedQuestion(null)
        closeConfirm()
      }
    })
  }

  const confirmDeleteAllNew = () => {
    setConfirmState({
      title: 'ยืนยันการลบทั้งหมด',
      message: `ต้องการลบข้อสอบใหม่ทั้งหมด ${newQs.length} ข้อ ใช่หรือไม่ ?`,
      onConfirm: () => {
        setQuestions(prev => prev.filter(q => !q._isNew))
        setSelectedNew(new Set())
        setEditingIndex(null)
        setEditedQuestion(null)
        closeConfirm()
      }
    })
  }



  const confirmDeleteSelectedOld = () => {
    const ids = [...selectedOld].map(i => oldQs[i].id)

    setConfirmState({
      title: 'ยืนยันการลบข้อสอบ',
      message: (
        <div>
          <p>ต้องการลบข้อสอบเดิมที่เลือก {ids.length} ข้อ ใช่หรือไม่</p>
          <p className="text-xs text-red-500 mt-1">
            * ข้อสอบที่ลบแล้วไม่สามารถกู้คืนได้
          </p>
        </div>
      ),
      onConfirm: async () => {
        await deleteQuestions(ids)

        setQuestions(prev => prev.filter(q => !ids.includes(q.id)))
        setSelectedOld(new Set())
        setEditingIndex(null)
        setEditedQuestion(null)
        closeConfirm()
      }
    })
  }


  const confirmDeleteAllOld = () => {
    const ids = oldQs.map(q => q.id)

    setConfirmState({
      title: 'ยืนยันการลบข้อสอบทั้งหมด',
      message: (
        <div>
          <p>ต้องการลบข้อสอบเดิมทั้งหมด {ids.length} ข้อ ใช่หรือไม่</p>
          <p className="text-xs text-red-600 mt-1 font-semibold">
            การลบนี้มีผลกับข้อมูลจริง
          </p>
        </div>
      ),
      onConfirm: async () => {
        await deleteQuestions(ids)

        setQuestions(prev => prev.filter(q => q._isNew))
        setSelectedOld(new Set())
        setEditingIndex(null)
        setEditedQuestion(null)
        closeConfirm()
      }
    })
  }



  const toggleSelectNew = (idx) => {
    setSelectedNew(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const toggleSelectOld = (idx) => {
    setSelectedOld(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }


  return (
    <div className="max-w-[1200px] mx-auto p-4 sm:p-6">
      <button
        className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-secondary mb-4"
        onClick={() => navigate(`/content/${cidRaw}/lessons/${lid}/bank`, { replace: true })}
      >
        <ArrowLeft size={18} /> กลับ
      </button>

      <Stepper current={step} onChange={setStep} />

      <AnimatePresence mode="wait">
        {step === 1 && (

          <motion.div key="step1" {...fadeUp} className="bg-white rounded-xl shadow-md p-4 sm:p-6">
            <div className="inline-flex gap-1 mb-4 rounded-xl bg-gray-100 p-1 shadow-inner">
              <button
                onClick={() => setSourceMode('file')}
                className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                transition-all duration-200
                ${sourceMode === 'file'
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 hover:bg-white hover:text-gray-900'
                  }
                `}
              >
                📄 ไฟล์ / ลิงก์
              </button>

              <button
                onClick={() => setSourceMode('custom')}
                className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                transition-all duration-200
                ${sourceMode === 'custom'
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 hover:bg-white hover:text-gray-900'
                  }
                `}
              >
                📝 ข้อสอบของฉัน
              </button>
            </div>


            <h2 className="text-lg font-semibold mb-4">1. อัปโหลดคลังความรู้</h2>
            {sourceMode === 'file' && (
              <KnowledgeUploader
                ref={uploaderRef}
                examId={lid}
                onUploaded={() => { }}
                onBusyChange={setUploadBusy}
                onSummaryStats={s => { setSumStats(s); setUnsumCount(s.unsummarized) }}
              />
            )}
            {sourceMode === 'custom' && (
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-500">
                <ImportExamModal
                  examId={lid}
                  value={customText}
                  onChange={setCustomText}
                  customId={customId}
                  setCustomId={setCustomId}
                  mode={customMode}
                  setMode={setCustomMode}
                  shuffleOptions={shuffleOptions}
                  setShuffleOptions={setShuffleOptions}
                />
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 text-sm font-semibold"
                onClick={handleNextFromStep1}
              >
                <ChevronRight /> ถัดไป
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="step2" {...fadeUp} className="bg-white rounded-xl shadow-md p-4 sm:p-6">
            <h2 className="text-lg font-semibold mb-4">2. ตั้งค่าและสร้างข้อสอบ</h2>

            <div className="mb-6 space-y-4">
              <div>
                <label className="flex items-center gap-1 text-sm"><Layers3 size={16} /> บทเรียน</label>
                <div className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                  {lessonName || '-'}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="flex items-center gap-1 text-sm"><Mountain size={16} /> ระดับความยาก</label>
                  <div className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                    {levelMapping[level]}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-1 text-sm"><MessageCircleQuestion size={16} /> ประเภทข้อสอบ</label>
                  <div className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                    {examTypeMapping[examType]}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-1 text-sm"><MessageCircleMore size={16} /> ประเภทคำตอบ</label>
                  <div className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                    {typeSummaryMapping[typeSummary]}
                  </div>
                </div>
              </div>
            </div>

            <CommonSettings
              generateButtonDisabled={uploadBusy || generating}
              generateButtonLabel={
                generating ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> กำลังสร้าง…</span>
                ) : (
                  'สร้างข้อสอบด้วย AI'
                )
              }
              onGenerateClick={handleGenerateClick}
              showResetButton={newQs.length > 0}
              onResetClick={handleRegenerate}
              KnowledgeUploaderEl={null}
              extraPrompt={extraPrompt}
              setExtraPrompt={setExtraPrompt}
              examType={examType}
              setExamType={setExamType}
              optionCount={optionCount}
              setOptionCount={setOptionCount}
              level={level}
              setLevel={setLevel}
              optionStyle={optionStyle}
              setOptionStyle={setOptionStyle}
              questionCount={questionCount}
              setQuestionCount={setQuestionCount}
              typeSummary={typeSummary}
              setTypeSummary={setTypeSummary}
              typeSummaryMin={typeSummaryMin}
              setTypeSummaryMin={setTypeSummaryMin}
              typeSummaryMax={typeSummaryMax}
              setTypeSummaryMax={setTypeSummaryMax}
              levelMapping={levelMapping}
              examTypeMapping={examTypeMapping}
              typeSummaryMapping={typeSummaryMapping}
              allowLanguage
              examLanguage={examLanguage}
              setExamLanguage={setExamLanguage}
              langs={LANGUAGES_AI}
            />

            <div className="flex items-center justify-between mt-6 gap-3 flex-wrap">
              <div className="text-xs text-gray-500">
                ปรับค่าด้านบนแล้วกด “สร้างข้อสอบด้วย AI” หรือไปขั้นตอนถัดไปได้เลย
              </div>
              <button
                className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg px-4 py-2 text-sm font-semibold"
                onClick={() => setStep(3)}
              >
                <ListIcon size={16} /> ไปจัดการข้อสอบ <span className="text-gray-500">(ไม่สร้างตอนนี้)</span>
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="step3" {...fadeUp} className="bg-white rounded-xl shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="text-lg font-semibold">3. จัดการข้อสอบทั้งหมด ({questions.length})</h2>
              <button
                onClick={() => setShowManualPopup(true)}
                className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm font-semibold"
              >
                <Plus size={16} /> เพิ่มข้อสอบด้วยตนเอง
              </button>
            </div>

            {newQs.length > 0 && (
              <>
                <h3 className="text-primary border-b-2 border-primary inline-block pb-1 text-base font-semibold mb-2">
                  ข้อสอบใหม่ ({newQs.length})
                  {newQs.length > 0 && (
                    <button
                      className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                      onClick={handleRegenerate}
                    >
                      <Redo2 size={14} /> สร้างใหม่
                    </button>
                  )}

                  <button
                    onClick={confirmDeleteSelectedNew}
                    className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                  >
                    <Trash2 size={14} />ลบที่เลือก ({selectedNew.size})
                  </button>

                  <button
                    onClick={confirmDeleteAllNew}
                    className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                  >
                    <Trash2 size={14} />ลบทั้งหมด
                  </button>
                </h3>
                <QuestionsCard
                  questions={newQs}
                  selectable
                  selected={selectedNew}
                  onToggleSelect={toggleSelectNew}
                  onDeleteSelected={confirmDeleteSelectedNew}
                  onDeleteAll={confirmDeleteAllNew}
                  {...makeCardProps(newMap)}
                />
              </>
            )}

            {oldQs.length > 0 && (
              <>
                <h3 className="text-primary border-b-2 border-primary inline-block pb-1 text-base font-semibold mb-2">
                  ข้อสอบเดิม ({oldQs.length})
                  <button
                    onClick={confirmDeleteSelectedOld}
                    className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                  >
                    <Trash2 size={14} />ลบที่เลือก ({selectedOld.size})
                  </button>

                  <button
                    onClick={confirmDeleteAllOld}
                    className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                  >
                    <Trash2 size={14} />ลบทั้งหมด
                  </button>
                </h3>
                <QuestionsCard
                  questions={oldQs}
                  selectable
                  selected={selectedOld}
                  onToggleSelect={toggleSelectOld}
                  onDeleteSelected={confirmDeleteSelectedOld}
                  onDeleteAll={confirmDeleteAllOld}
                  {...makeCardProps(oldMap)}
                />

              </>
            )}

            <div className="flex justify-end mt-6">
              <button
                disabled={saving}
                onClick={saveToDB}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg px-5 py-2 text-sm font-semibold"
              >
                {saving ? 'กำลังบันทึก…' : 'บันทึกชุดข้อสอบ'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual add popup */}
      <AnimatePresence>
        {showManualPopup && (
          <motion.div
            key="manual"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[999]"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-xl w-full max-w-2xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4">เพิ่มข้อสอบด้วยตนเอง</h3>
              <ManualQuestionForm
                typeSummary={typeSummary}
                manualOptions={manualOptions}
                setManualOptions={setManualOptions}
                manualCorrect={manualCorrect}
                setManualCorrect={setManualCorrect}
                optionCount={optionCount}
                typeSummaryMin={typeSummaryMin}
                typeSummaryMax={typeSummaryMax}
                newQuestionText={newQuestionText}
                setNewQuestionText={setNewQuestionText}
                addManualQuestion={addManualQuestion}
                openAlert={alert}
                hideAddButton
              />
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 text-sm font-semibold"
                  onClick={addManualQuestion}
                >
                  บันทึก
                </button>
                <button
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg px-4 py-2 text-sm font-semibold"
                  onClick={() => setShowManualPopup(false)}
                >
                  ยกเลิก
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm regenerate */}
      <AnimatePresence>
        {confirmRe && (
          <ConfirmModal
            type="confirm"
            title="ยืนยันสร้างใหม่"
            message="ต้องการสร้างข้อสอบชุดใหม่หรือไม่?"
            onConfirm={doGenerate}
            onCancel={() => setConfirmRe(false)}
          />
        )}
      </AnimatePresence>

      {/* Confirm generate */}
      <AnimatePresence>
        {confirmGen && (
          <motion.div
            key="gen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[999]"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-xl w-full max-w-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4">ยืนยันสร้างข้อสอบ</h3>
              {genSummary()}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 text-sm font-semibold"
                  onClick={() => { setConfirmGen(false); doGenerate() }}
                >
                  ยืนยัน
                </button>
                <button
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg px-4 py-2 text-sm font-semibold"
                  onClick={() => setConfirmGen(false)}
                >
                  ยกเลิก
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary gate (Step 1 -> Step 2) */}
      <AnimatePresence>
        {showSummaryGate && (
          <motion.div
            key="sum-gate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 14 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.98, opacity: 0, y: 8 }}
              transition={{ duration: 0.22 }}
              className="bg-white rounded-2xl w-full max-w-lg p-6"
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 rounded-full bg-yellow-100 text-yellow-700 p-2">
                  <AlertTriangle size={18} />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold mb-2">ยังมีไฟล์ที่ไม่ได้สรุป</h3>
                  <p className="text-sm text-gray-700">
                    พบไฟล์ที่ยังไม่ได้สรุปจำนวน <b>{unsumCount}</b> ไฟล์ จากทั้งหมด {sumStats.total} ไฟล์
                  </p>
                  <ul className="text-xs text-gray-600 mt-2 space-y-1">
                    <li>• สรุปก่อนเพื่อให้ AI นำเนื้อหาไปสร้างข้อสอบได้แม่นยำ</li>
                    <li>• หรือไปต่อเพื่อใช้วิธีสร้าง/เพิ่มข้อสอบด้วยตนเอง</li>
                  </ul>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 text-sm font-semibold"
                  onClick={() => {
                    setShowSummaryGate(false)
                    uploaderRef.current?.openSummarize()
                  }}
                >
                  <Brain size={16} /> ไปสรุปก่อน
                </button>
                <button
                  className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg px-4 py-2 text-sm font-semibold"
                  onClick={() => {
                    setShowSummaryGate(false)
                    setStep(2)
                  }}
                >
                  <ChevronRight size={16} /> ไปต่อ
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Alerts */}
      <AnimatePresence>
        {showAlert && (
          <ConfirmModal
            type="alert"
            title="แจ้งเตือน"
            message={alertMsg}
            onClose={() => setShowAlert(false)}
          />
        )}

        {confirmState && (
          <ConfirmModal
            type="confirm"
            title={confirmState.title}
            message={confirmState.message}
            onConfirm={confirmState.onConfirm}
            onCancel={closeConfirm}
            onClose={closeConfirm}
          />
        )}

      </AnimatePresence>
    </div>
  )
}
